import { useState, useEffect, useRef, useCallback } from "react";
import { X, Share } from "lucide-react";

const STORAGE_KEY = "ios_install_banner_dismissed";
const DISMISS_DAYS = 30;
/** Minimum downward swipe distance (px) to trigger dismiss */
const SWIPE_DISMISS_THRESHOLD = 60;
/** Maximum horizontal drift (px) allowed while still counting as a vertical swipe */
const SWIPE_MAX_HORIZONTAL = 40;

const COPY = {
  en: {
    title: "Install this app",
    instruction: "Add to Home Screen",
    tapPrefix: "Tap",
    thenPrefix: "then",
  },
  es: {
    title: "Instalar esta app",
    instruction: "Agregar a Inicio",
    tapPrefix: "Toca",
    thenPrefix: "luego",
  },
} as const;

function getLocale(): "en" | "es" {
  try {
    const lang = navigator.language || (navigator.languages && navigator.languages[0]) || "en";
    return lang.startsWith("es") ? "es" : "en";
  } catch {
    return "en";
  }
}

/**
 * Detects if the user is on iOS Safari (not in standalone/PWA mode)
 * and shows a subtle, dismissible banner suggesting they add the app
 * to their Home Screen.
 *
 * Animation: springs up from below the mobile nav bar on entry,
 * slides back down on dismiss (via button tap OR swipe-down gesture).
 * During a swipe the banner tracks the finger in real time; releasing
 * past the threshold snaps it out, releasing early snaps it back.
 */
export function IOSInstallBanner() {
  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(false);

  // Live drag offset while the user is swiping (positive = dragged down)
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const touchStartY = useRef(0);
  const touchStartX = useRef(0);
  const isTracking = useRef(false);

  useEffect(() => {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent));
    const isStandalone =
      ("standalone" in window.navigator &&
        (window.navigator as unknown as { standalone: boolean }).standalone) ||
      window.matchMedia("(display-mode: standalone)").matches;
    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      !/CriOS|FxiOS|OPiOS|EdgiOS/.test(navigator.userAgent);

    if (!isIOS || isStandalone || !isSafari) return;

    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (dismissed) {
        const dismissedAt = parseInt(dismissed, 10);
        const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
        if (daysSince < DISMISS_DAYS) return;
      }
    } catch {
      // localStorage unavailable — show banner anyway
    }

    const mountTimer = setTimeout(() => {
      setReady(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    }, 3000);

    return () => clearTimeout(mountTimer);
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    setDragY(0);
    setIsDragging(false);
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {
      // silently fail
    }
    setTimeout(() => setReady(false), 420);
  }, []);

  // ── Touch handlers ────────────────────────────────────────────────────────

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!visible) return;
    const touch = e.touches[0];
    touchStartY.current = touch.clientY;
    touchStartX.current = touch.clientX;
    isTracking.current = true;
    setIsDragging(false);
  }, [visible]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isTracking.current || !visible) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - touchStartY.current;
    const deltaX = Math.abs(touch.clientX - touchStartX.current);

    // Ignore if horizontal drift is too large (user is scrolling sideways)
    if (deltaX > SWIPE_MAX_HORIZONTAL) {
      isTracking.current = false;
      setDragY(0);
      setIsDragging(false);
      return;
    }

    // Only track downward swipes
    if (deltaY > 0) {
      // Apply a rubber-band resistance so it feels tethered
      const resistance = 0.55;
      const rubberY = deltaY * resistance;
      setDragY(rubberY);
      setIsDragging(true);
      // Prevent the page from scrolling while we're tracking
      e.preventDefault();
    } else {
      // Upward swipe — reset
      setDragY(0);
      setIsDragging(false);
    }
  }, [visible]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!isTracking.current) return;
    isTracking.current = false;

    const touch = e.changedTouches[0];
    const rawDeltaY = touch.clientY - touchStartY.current;

    if (rawDeltaY >= SWIPE_DISMISS_THRESHOLD) {
      // Crossed the threshold — dismiss
      dismiss();
    } else {
      // Didn't cross — snap back
      setDragY(0);
      setIsDragging(false);
    }
  }, [dismiss]);

  if (!ready) return null;

  const locale = getLocale();

  // During a drag: follow the finger directly (no transition).
  // When not dragging: use the spring/ease-in transitions.
  const translateY = isDragging
    ? dragY
    : visible
    ? 0
    : "calc(100% + 24px)";

  const transitionStyle = isDragging
    ? "none"
    : visible
    ? "transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 300ms ease"
    : "transform 350ms cubic-bezier(0.4, 0, 1, 1), opacity 250ms ease";

  return (
    <div
      className="fixed left-4 right-4 z-[60] md:hidden"
      style={{
        bottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
        transform: `translateY(${typeof translateY === "number" ? `${translateY}px` : translateY})`,
        opacity: visible ? 1 : 0,
        transition: transitionStyle,
        willChange: "transform, opacity",
        // Prevent page scroll while tracking a swipe on the banner
        touchAction: isDragging ? "none" : "pan-x",
      }}
      role="alert"
      aria-live="polite"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="bg-card/95 backdrop-blur-md border border-border rounded-2xl shadow-xl px-4 py-3.5 flex items-start gap-3">
        {/* Drag handle pill — visual affordance for the swipe gesture */}
        <div
          className="absolute top-2 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-border opacity-60"
          aria-hidden="true"
        />

        {/* App icon */}
        <img
          src="https://d2xsxph8kpxj0f.cloudfront.net/310519663270229297/i88gywhNYafBWiKD3FZzYw/lb-logo_6a402776.png"
          alt="Redefining Normal"
          className="w-10 h-10 rounded-xl shrink-0 shadow-sm mt-2"
        />

        {/* Content */}
        <div className="flex-1 min-w-0 mt-2">
          <p className="text-sm font-semibold text-foreground leading-tight">
            {COPY[locale].title}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
            {COPY[locale].tapPrefix}{" "}
            <Share className="inline w-3.5 h-3.5 -mt-0.5 text-primary" />{" "}
            {COPY[locale].thenPrefix}{" "}
            <span className="font-medium text-foreground">
              "{COPY[locale].instruction}"
            </span>
          </p>
        </div>

        {/* Dismiss button */}
        <button
          onClick={dismiss}
          className="shrink-0 p-1.5 -mr-1 rounded-full hover:bg-muted/80 active:bg-muted/80 active:scale-90 transition-all mt-2"
          aria-label="Dismiss install banner"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}
